import axios from "axios";
import { APP_API_PREFIX, APP_BACKEND_URL, NODE_ENV } from "@/lib/env";

function getDefaultBackendUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
}

const DEFAULT_BACKEND_URL = getDefaultBackendUrl();
const LEGACY_MOCK_STORAGE_KEYS = [
  "rsi_admin_mock_user",
  "rsi_admin_mock_settings",
  "rsi_admin_mock_password",
  "rsi_admin_mock_categories",
  "rsi_admin_mock_products",
  "rsi_admin_mock_certificates",
  "rsi_admin_mock_shipments",
  "rsi_admin_mock_orders",
  "rsi_admin_mock_app_subscriptions",
  "rsi_admin_mock_app_notifications",
  "rsi_admin_mock_tax_profiles",
  "rsi_admin_mock_analytics_events",
];

const RAW_BACKEND_URL = APP_BACKEND_URL;
export const BACKEND_URL = (RAW_BACKEND_URL !== undefined ? RAW_BACKEND_URL : DEFAULT_BACKEND_URL).replace(/\/$/, "");
export const IS_MOCK_BACKEND = false;
const API_PREFIX = APP_API_PREFIX ?? (BACKEND_URL.endsWith("/api") ? "" : "/api");
export const API_BASE = `${BACKEND_URL}${API_PREFIX}`;

function getSessionStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getLocalStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function clearLegacyMockAdminState() {
  const local = getLocalStorage();
  const session = getSessionStorage();
  if (!local && !session) return;
  LEGACY_MOCK_STORAGE_KEYS.forEach((key) => {
    local?.removeItem(key);
    session?.removeItem(key);
  });
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Allow Bearer token fallback (mobile Safari sometimes drops third-party cookies)
const TOKEN_KEY = "rsi_admin_token";
export const setAuthToken = (token) => {
  const session = getSessionStorage();
  const local = getLocalStorage();
  if (token) {
    session?.setItem(TOKEN_KEY, token);
  } else {
    session?.removeItem(TOKEN_KEY);
  }
  local?.removeItem(TOKEN_KEY);
};

export const getAuthToken = () => {
  const session = getSessionStorage();
  const local = getLocalStorage();
  const currentToken = session?.getItem(TOKEN_KEY);
  if (currentToken) return currentToken;

  const legacyToken = local?.getItem(TOKEN_KEY);
  if (!legacyToken) return null;

  session?.setItem(TOKEN_KEY, legacyToken);
  local?.removeItem(TOKEN_KEY);
  return legacyToken;
};

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((entry) => (entry && typeof entry.msg === "string" ? entry.msg : JSON.stringify(entry)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function toBackendUrl(path) {
  if (!path) return path;
  return path.startsWith("http") ? path : `${BACKEND_URL}${path}`;
}
